/**
 * M2 onboarding: Step 1 — org name (with search typeahead) + EIN (required for nonprofits), timezone.
 * First name, last name (and phone when available) are sourced from Clerk and seeded in OrganizationSetup.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import OnboardingStepLayout from '../shared/OnboardingStepLayout';
import {
  callEINLookup,
  callWebsiteEnrich,
  normalizeWebsiteUrl,
  INPUT_CLASS,
  SELECT_CLASS,
} from './orgSetupUtils';
import { ENTITY_TYPE_NONPROFIT, ENTITY_TYPE_INDIVIDUAL } from '../shared/useOnboardingState';
import {
  buildNonprofitLookupPayload,
  getEinValidationError,
} from './orgSetupEinPolicy';
import { buildIndividualEntityPatch, getEffectiveOrgSlug } from './orgSetupEntityScope';
import { useOrgAvailability } from './useOrgAvailability';
import OnboardingLoadingPattern from '../../OnboardingLoadingPattern';
import { safeCapture } from '../../../utils/analytics';
import { BACK_TO_DASHBOARD_LABEL } from './onboardingCopy';

const SEARCH_DEBOUNCE_MS = 350;

// PE-2048: the org's URL name (slug) — auto-suggested from the name until the
// user edits it, then their value wins. Sent to create-org as
// organizationSlug; Clerk enforces global uniqueness at create time.
function OrgUrlNameField({ state, update, inputClass, error, requestError, checking, onRetry }) {
  const value = getEffectiveOrgSlug(state);
  const messageId = 'onb-org-slug-message';
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-ink-secondary">
        Organization Slug
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
          update({ orgSlug: sanitized, orgSlugTouched: true });
        }}
        placeholder="your-organization"
        className={`${inputClass} ${error || requestError ? 'border-status-red-text/50' : ''}`}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={Boolean(error || requestError)}
        aria-describedby={messageId}
      />
      <div id={messageId}>
        {error || requestError ? (
          <p className="text-xs text-status-red-text" role="alert">
            {error || requestError}
            {requestError && (
              <button type="button" onClick={onRetry} className="ml-1 underline">
                Try again
              </button>
            )}
          </p>
        ) : checking ? (
          <p className="text-xs text-ink-muted" role="status">Checking availability…</p>
        ) : (
          <p className="text-xs text-ink-muted">
            Your organization&apos;s unique slug on CharityStack — used in your
            public links (e.g. charitystack.com/
            {value || 'your-organization'}/privacy). Lowercase letters, numbers,
            and hyphens.
          </p>
        )}
      </div>
    </div>
  );
}

export default function OrgSetupStep1({
  state,
  update,
  setStep,
  onBack,
  onCancel,
  posthog,
}) {
  const { firstName, lastName, phoneNumber, orgName, ein, timezone, causeCategory, entityType } = state;
  const isNonprofit = entityType !== ENTITY_TYPE_INDIVIDUAL;
  const [error, setError] = React.useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const enrichRequestIdRef = useRef(0);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const tabBarRef = useRef(null);
  const tab0Ref = useRef(null);
  const tab1Ref = useRef(null);
  const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0 });
  const organizationSlug = getEffectiveOrgSlug(state);
  const availability = useOrgAvailability({ orgName, organizationSlug });

  // Measure tab positions for sliding indicator
  useEffect(() => {
    const updateIndicator = () => {
      const activeRef = isNonprofit ? tab0Ref : tab1Ref;
      const bar = tabBarRef.current;
      const tab = activeRef.current;
      if (bar && tab) {
        const barRect = bar.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        setTabIndicator({ left: tabRect.left - barRect.left, width: tabRect.width });
      }
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [isNonprofit]);

  useEffect(() => {
    safeCapture(posthog, 'onboarding_org_setup_started', {
      step: 1,
      step_name: 'org_setup_step_1',
    });
    console.log('[Onboarding] Step 1 mounted');
  }, [posthog]);

  const runSearch = useCallback((query) => {
    const term = (query || '').trim();
    if (term.length < 2) {
      setSearchResults([]);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    setDropdownOpen(true);
    console.log('[Onboarding] Step 1 org search (by name)', { term });
    callEINLookup({ query: term })
      .then((res) => {
        const list = res?.results && Array.isArray(res.results) ? res.results : (res?.found ? [res] : []);
        // Limit to 8 results to match dropdown height
        const limitedList = list.slice(0, 8);
        setSearchResults(limitedList);
        console.log('[Onboarding] Step 1 search results', limitedList.length, `of ${list.length}`);
      })
      .catch((err) => {
        console.warn('[Onboarding] Step 1 search failed', err);
        setSearchResults([]);
      })
      .finally(() => setSearching(false));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Only run Every.org typeahead for registered nonprofits — individuals
    // don't have an EIN so the lookup has no value and wastes API calls.
    if (!isNonprofit) {
      setSearchResults([]);
      return;
    }
    const term = (orgName || '').trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [orgName, isNonprofit, runSearch]);

  useEffect(() => {
    function handleClickOutside(e) {
      const anchor = inputRef.current?.closest?.('.js-org-search-anchor');
      if (
        anchor && !anchor.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position dropdown when it opens; update on scroll/resize so it stays under the input
  useEffect(() => {
    if (!dropdownOpen || !(searching || searchResults.length > 0) || !inputRef.current) {
      setDropdownPosition(null);
      return;
    }
    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [dropdownOpen, searching, searchResults.length]);

  const selectResult = (item) => {
    console.log('[Onboarding] Step 1 selected org', item?.legalName);
    // Close dropdown BEFORE updating state to prevent re-triggering search
    setDropdownOpen(false);
    setSearchResults([]);

    const parsedEin = item.ein && String(item.ein).replace(/\D/g, '').length === 9 ? item.ein : '';
    const websiteUrlFromApi = item.websiteUrl || item.website || '';
    // Only auto-fill website when the hit includes a valid EIN (API match is firm)
    const websiteFromApiWhenEin = parsedEin ? websiteUrlFromApi : '';
    const updatePayload = {
      orgName: item.legalName || item.orgName || state.orgName,
      legalName: item.legalName || '',
      ein: parsedEin || state.ein,
      city: item.city || '',
      state: item.state || '',
      nteeCode: item.nteeCode || '',
      causeCategory: item.causeCategory || '',
      description: item.description || '', // Clear old description, use new one or empty
      logoUrl: item.logoUrl || '', // Clear old logo, use new one or empty
      ogImage: '', // Clear old OG image
      websiteUrl: websiteFromApiWhenEin,
      hasWebsite: !!websiteFromApiWhenEin,
      einLookupStatus: 'found',
      einLookupPayload: item,
      // Every.org only returns tax-exempt orgs — a match with a valid EIN confirms tax-exempt.
      // When no valid EIN is found, preserve the user's existing declaration (don't downgrade).
      isTaxExempt: parsedEin ? true : state.isTaxExempt,
    };
    console.log('[Onboarding] Step 1 update payload', updatePayload);
    update(updatePayload);
    const urlFromSelection = normalizeWebsiteUrl(websiteFromApiWhenEin);
    if (urlFromSelection) {
      const reqId = ++enrichRequestIdRef.current;
      safeCapture(posthog, 'onboarding_website_enrichment_attempted', {
        step: 1,
        step_name: 'org_setup_step_1',
        url: urlFromSelection,
      });
      update({ websiteEnrichStatus: 'loading' });
      callWebsiteEnrich({ url: urlFromSelection })
        .then((res) => {
          if (reqId !== enrichRequestIdRef.current) return;
          if (res && res.ok && res.data) {
            const d = res.data;
            const finalDesc = d.description || d.ogDescription || state.description || '';
            console.log(`[Onboarding] Website enrichment: d.description=${d.description ? 'YES (' + d.description.length + ' chars)' : 'NO'}, d.ogDescription=${d.ogDescription ? 'YES (' + d.ogDescription.length + ' chars)' : 'NO'}, final=${finalDesc.length} chars`);
            update({
              websiteEnrichStatus: 'ok',
              ogImage: d.ogImage || '',
              favicon: d.favicon || '',
              suggestedBrandColor: d.brandColor || '',
              description: finalDesc,
            });
            safeCapture(posthog, 'onboarding_website_enrichment_succeeded', {
              step: 1,
              step_name: 'org_setup_step_1',
              fields_extracted: ['ogImage', 'brandColor', 'description'].filter((k) => d[k]),
            });
          } else {
            update({ websiteEnrichStatus: 'error' });
          }
        })
        .catch(() => {
          if (reqId !== enrichRequestIdRef.current) return;
          update({ websiteEnrichStatus: 'error' });
        });
    } else {
      update({ websiteEnrichStatus: 'idle' });
    }
  };

  const handleContinue = () => {
    setError('');
    if (!firstName?.trim() || !lastName?.trim()) {
      setError('Your first and last name is required.');
      return;
    }
    if (!phoneNumber?.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (!orgName?.trim()) {
      setError(isNonprofit
        ? 'Please enter your nonprofit\'s name.'
        : 'Please enter your organization\'s name.');
      return;
    }
    if (!availability.canContinue) {
      setError(availability.requestError || 'Please resolve the organization name or slug error to continue.');
      return;
    }
    const einError = getEinValidationError({ entityType, ein });
    if (einError) {
      setError(einError);
      return;
    }
    if (!causeCategory) {
      setError('Please select your organization type.');
      return;
    }
    // Nonprofits must answer the tax-exempt question explicitly
    if (isNonprofit && state.isTaxExempt !== true && state.isTaxExempt !== false) {
      setError('Please answer whether your organization has tax-exempt status to continue.');
      return;
    }
    console.log('[Onboarding] Step 1 continue', { firstName, lastName, orgName: orgName || '(optional)', hasWebsite: state.hasWebsite });
    safeCapture(posthog, 'onboarding_persona_selected', {
      step: 1,
      step_name: 'org_setup_step_1',
      hasWebsite: state.hasWebsite,
      isTaxExempt: state.isTaxExempt,
    });
    const userWebsiteInput = (state.websiteUrl || '').trim();
    update({
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    });
    setStep(2);

    const runEnrichFromUrl = (rawUrl, logLabel) => {
      const url = normalizeWebsiteUrl(rawUrl);
      if (!url) return;
      const reqId = ++enrichRequestIdRef.current;
      safeCapture(posthog, 'onboarding_website_enrichment_attempted', {
        step: 1,
        step_name: 'org_setup_step_1',
        url,
      });
      update({ websiteEnrichStatus: 'loading' });
      callWebsiteEnrich({ url })
        .then((enrichRes) => {
          if (reqId !== enrichRequestIdRef.current) return;
          if (enrichRes?.ok && enrichRes.data) {
            const d = enrichRes.data;
            const finalDesc = d.description || d.ogDescription || state.description || '';
            console.log(`[Onboarding] Website enrichment (${logLabel}): d.description=${d.description ? 'YES (' + d.description.length + ' chars)' : 'NO'}, d.ogDescription=${d.ogDescription ? 'YES (' + d.ogDescription.length + ' chars)' : 'NO'}, final=${finalDesc.length} chars`);
            update({
              websiteEnrichStatus: 'ok',
              ogImage: d.ogImage || '',
              favicon: d.favicon || '',
              suggestedBrandColor: d.brandColor || '',
              description: finalDesc,
            });
            safeCapture(posthog, 'onboarding_website_enrichment_succeeded', {
              step: 1,
              step_name: 'org_setup_step_1',
              fields_extracted: ['ogImage', 'brandColor', 'description'].filter((k) => d[k]),
            });
          } else {
            update({ websiteEnrichStatus: 'error' });
          }
        })
        .catch(() => {
          if (reqId !== enrichRequestIdRef.current) return;
          update({ websiteEnrichStatus: 'error' });
        });
    };

    const lookupPayload = buildNonprofitLookupPayload({
      entityType,
      ein,
      orgName,
      einLookupStatus: state.einLookupStatus,
    });

    if (lookupPayload) {
      update({ einLookupStatus: 'loading' });
      callEINLookup(lookupPayload).catch(() => {}).then((res) => {
        const list = res?.results && Array.isArray(res.results) ? res.results : (res?.found ? [res] : []);
        const first = list[0] || res;
        if (first && (first.legalName || first.ein)) {
          const parsedEin = first.ein ? String(first.ein).replace(/\D/g, '').length === 9 ? first.ein : '' : '';
          const websiteUrlFromApi = first.websiteUrl || first.website || '';
          const mergedWebsite = userWebsiteInput || (parsedEin ? websiteUrlFromApi : '') || '';
          update({
            einLookupStatus: 'found',
            einLookupPayload: first,
            legalName: first.legalName || '',
            ein: parsedEin || state.ein,
            city: first.city || '',
            state: first.state || '',
            nteeCode: first.nteeCode || '',
            causeCategory: first.causeCategory || '',
            description: first.description || state.description,
            logoUrl: first.logoUrl || state.logoUrl,
            websiteUrl: mergedWebsite,
            hasWebsite: !!mergedWebsite,
            // Every.org only returns tax-exempt orgs — a match with valid EIN confirms tax-exempt.
            // Never downgrade — the user's explicit declaration is authoritative.
            isTaxExempt: parsedEin ? true : state.isTaxExempt,
          });
          console.log('[Onboarding] Step 1 post-continue lookup found', first.legalName);
          runEnrichFromUrl(mergedWebsite, 'post-continue');
        } else {
          update({ einLookupStatus: 'not_found' });
          if (userWebsiteInput) {
            runEnrichFromUrl(userWebsiteInput, 'post-continue-not-found');
          }
        }
      });
    } else if (
      userWebsiteInput &&
      state.websiteEnrichStatus !== 'ok' &&
      state.websiteEnrichStatus !== 'loading'
    ) {
      runEnrichFromUrl(
        userWebsiteInput,
        isNonprofit ? 'continue-manual' : 'continue-individual',
      );
    }
  };

  const handleCancel = () => {
    // Ignore any enrichment response that returns after the user leaves, so
    // resuming this scoped draft never starts on a stale loading state.
    enrichRequestIdRef.current += 1;
    if (state.websiteEnrichStatus === 'loading') {
      update({ websiteEnrichStatus: 'idle' });
    }
    onCancel?.();
  };

  return (
    <OnboardingStepLayout
      showProgressBar={false}
      step={1}
      totalSteps={2}
      title="Your Info"
      onBack={onBack}
      error={error}
      ctaLabel="Continue"
      onCta={handleContinue}
      ctaDisabled={!orgName?.trim() || !availability.canContinue}
      secondaryActionLabel={onCancel ? BACK_TO_DASHBOARD_LABEL : undefined}
      onSecondaryAction={onCancel ? handleCancel : undefined}
    >
      <div className="gap-10 pt-4 flex flex-col pb-2">
        {/* Personal Info — name from Clerk (read-only), phone editable */}
        <div className="rounded-lg bg-paper-muted space-y-4 gap-2 border-edge">
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <label htmlFor="onb-first-name" className="block text-sm font-medium text-ink-secondary">First name</label>
              <input
                id="onb-first-name"
                name="given-name"
                type="text"
                value={firstName}
                onChange={(e) => update({ firstName: e.target.value })}
                placeholder="First name"
                className={INPUT_CLASS}
                autoComplete="given-name"
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <label htmlFor="onb-last-name" className="block text-sm font-medium text-ink-secondary">Last name</label>
              <input
                id="onb-last-name"
                name="family-name"
                type="text"
                value={lastName}
                onChange={(e) => update({ lastName: e.target.value })}
                placeholder="Last name"
                className={INPUT_CLASS}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="onb-phone" className="block text-sm font-medium text-ink-secondary">Phone number</label>
            <input
              id="onb-phone"
              name="tel"
              type="tel"
              value={phoneNumber || ''}
              onChange={(e) => update({ phoneNumber: e.target.value })}
              className={INPUT_CLASS}
              autoComplete="tel"
            />
          </div>
        </div>

        <div className="bg-paper space-y-5 pt-8 border-t-2 border-solid border-t-[#9A9285]">
          <h3 className="text-xl font-semibold text-[#3A3429]">Your Organization</h3>

          {/* Entity type tabs with sliding indicator */}
          <div className="relative">
            <div
              ref={tabBarRef}
              className="grid grid-cols-2 gap-6 border-b border-[#E5E0D8] relative"
              role="tablist"
            >
              <button
                ref={tab0Ref}
                role="tab"
                aria-selected={isNonprofit}
                type="button"
                className={`pb-3 text-left w-full border-0 bg-transparent shadow-none outline-none ring-0 rounded-none transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${isNonprofit ? 'text-ink' : 'text-ink-muted hover:text-ink-secondary'}`}
                onClick={() => {
                  update({ entityType: ENTITY_TYPE_NONPROFIT, isTaxExempt: true });
                  setSearchResults([]);
                  setDropdownOpen(false);
                }}
              >
                <div className={`text-sm font-semibold mb-1 ${isNonprofit ? 'text-ink' : 'text-ink-muted'}`}>
                  I'm a Registered Organization
                </div>
                <div className="flex items-center gap-1 text-xs text-ink-muted">
                  <span> An incorporated entity with an EIN</span>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-ink-muted hover:text-ink-secondary transition-colors cursor-help shrink-0"
                          aria-label="What counts as a Registered Nonprofit?"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="font-medium mb-1">Registered Organizations</p>
                        <p className="text-xs">Your org is registered by the state. This includes Registered Nonprofits with and without 501(c)(3), Tax-Exempt Organizations (501(c)(4), 501(c)(6), 501(c)(7), 501(c)(19)), and any other tax-exempt entities.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </button>
              <button
                ref={tab1Ref}
                role="tab"
                aria-selected={!isNonprofit}
                type="button"
                className={`pb-3 text-left w-full border-0 bg-transparent shadow-none outline-none ring-0 rounded-none transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${!isNonprofit ? 'text-ink' : 'text-ink-muted hover:text-ink-secondary'}`}
                onClick={() => {
                  enrichRequestIdRef.current += 1;
                  update(buildIndividualEntityPatch());
                  setSearchResults([]);
                  setDropdownOpen(false);
                }}
              >
                <div className={`text-sm font-semibold mb-1 ${!isNonprofit ? 'text-ink' : 'text-ink-muted'}`}>
                  I'm not a Registered Organization
                </div>
                <div className="flex items-center gap-1 text-xs text-ink-muted">
                  <span>Not an incorporated entity</span>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-ink-muted hover:text-ink-secondary transition-colors cursor-help shrink-0"
                          aria-label="Who falls in this category?"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="font-medium mb-1">Who this is for</p>
                        <p className="text-xs">Individuals raising funds for personal causes (memorials, medical, mission trips), unincorporated clubs (neighborhood associations, hobby groups), and new organizations pending IRS approval.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </button>
              {/* Sliding underline indicator */}
              <div
                className="absolute bottom-0 h-0.5 rounded-full bg-[#C9C2B6] transition-all duration-250"
                style={{
                  left: tabIndicator.left,
                  width: tabIndicator.width,
                  transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
                }}
              />
            </div>
          </div>

          {/* Nonprofit: Org name (with search typeahead) + EIN field */}
          {isNonprofit && (
            <>
              <div className="space-y-1.5">
                <div className="relative z-50 js-org-search-anchor">
                  <label className="block text-sm font-medium text-ink-secondary pb-1.5">What is your Nonprofit's name?</label>
                  <input
                    ref={inputRef}
                    type="text"
                    value={orgName}
                    onChange={(e) => update({ orgName: e.target.value })}
                    onFocus={() => searchResults.length > 0 && setDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setDropdownOpen(false);
                        inputRef.current?.blur();
                      }
                    }}
                    placeholder="e.g. Red Cross"
                    className={`${INPUT_CLASS} ${availability.nameError ? 'border-status-red-text/50' : ''}`}
                    autoComplete="off"
                    aria-invalid={Boolean(availability.nameError)}
                    aria-describedby={availability.nameError ? 'onb-org-name-error' : undefined}
                  />
                  {dropdownOpen && dropdownPosition && typeof document !== 'undefined' && createPortal(
                    <div ref={dropdownRef} style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width, zIndex: 9999 }}>
                      <ul
                        className="rounded-md border border-edge bg-paper-light shadow-lg max-h-[480px] overflow-y-auto overflow-x-hidden animate-in fade-in-0 zoom-in-95"
                        role="listbox"
                      >
                        {searching ? (
                          <li className="px-4 py-3 text-sm text-ink-muted bg-paper-light min-h-[120px]">
                            <OnboardingLoadingPattern
                              compact
                              showLogo={false}
                              title="Searching organizations"
                              messages={[
                                'Looking up registered nonprofits…',
                                'Matching names in our directory…',
                              ]}
                              messageIntervalMs={2200}
                              className="w-full items-stretch"
                            />
                          </li>
                        ) : searchResults.length > 0 ? (
                          searchResults.map((r, i) => (
                            <li
                              key={i}
                              role="option"
                              aria-selected={false}
                              tabIndex={0}
                              className="px-4 py-2 text-xs text-ink cursor-pointer bg-paper-light hover:bg-paper-muted transition-colors border-b border-edge last:border-b-0"
                              onClick={() => {
                                selectResult(r);
                                setDropdownOpen(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  selectResult(r);
                                  setDropdownOpen(false);
                                }
                              }}
                            >
                              <div className="font-medium text-sm">{r.legalName || r.name || 'Unknown'}</div>
                              {(r.city || r.state) && (
                                <div className="text-xs text-ink-muted">
                                  {[r.city, r.state].filter(Boolean).join(', ')}
                                </div>
                              )}
                            </li>
                          ))
                        ) : (
                          <li className="px-4 py-3 text-sm text-ink-muted bg-paper-light">
                            {orgName.trim().length >= 2
                              ? 'No matches found.'
                              : 'Start typing to search'}
                          </li>
                        )}
                      </ul>
                    </div>,
                    document.body
                  )}
                </div>
                {availability.nameError && (
                  <p id="onb-org-name-error" className="text-xs text-status-red-text" role="alert">
                    {availability.nameError}
                  </p>
                )}
              </div>

              {/* PE-2048: URL name (slug), auto-suggested from the org name */}
              <OrgUrlNameField
                state={state}
                update={update}
                inputClass={INPUT_CLASS}
                error={availability.slugError}
                requestError={availability.requestError}
                checking={availability.checking}
                onRetry={availability.retry}
              />

              {/* Website — optional for nonprofits and non-nonprofits (manual or pre-filled from EIN hit) */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">Website (recommended)</label>
                <input
                  type="text"
                  inputMode="url"
                  value={state.websiteUrl || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    enrichRequestIdRef.current += 1;
                    const patch = { websiteUrl: v, hasWebsite: !!v.trim(), websiteEnrichStatus: 'idle' };
                    if (!v.trim()) {
                      patch.ogImage = '';
                      patch.favicon = '';
                      patch.suggestedBrandColor = '';
                    }
                    update(patch);
                  }}
                  placeholder="yourorganization.org"
                  className={INPUT_CLASS}
                  autoComplete="off"
                />
              </div>

              {/* EIN — auto-filled from search selection, or manual entry */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <label className="block text-sm font-medium text-ink-secondary">EIN</label>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-ink-muted hover:text-ink-secondary transition-colors cursor-help"
                          aria-label="What is an EIN?"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <div className="space-y-1.5">
                          <p className="font-medium">What is an EIN?</p>
                          <p className="text-xs">
                            Your EIN (Employer Identification Number) is a unique 9-digit number assigned by the IRS to identify your organization. Format: XX-XXXXXXX.
                          </p>
                          <p className="text-xs">
                            Find it on your <span className="font-medium">IRS SS-4 confirmation letter</span>, Letter 147C, Form 990, or other official IRS correspondence.
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <input
                  type="text"
                  value={ein || ''}
                  onChange={(e) => update({ ein: e.target.value })}
                  placeholder="12-3456789"
                  className={`${INPUT_CLASS} ${ein ? 'border-sage/50' : ''}`}
                  autoComplete="off"
                  required
                  aria-required="true"
                />
              </div>

              {/* Tax-exempt status — single question replaces old 501(c)(3) + sub-question */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-ink-secondary">
                  Does your organization have tax-exempt status?
                </label>
                <p className="text-xs text-ink-muted -mt-0.5">
                  e.g. 501(c)(3), 501(c)(4), 501(c)(6), 501(c)(7), 501(c)(19)
                </p>
                <div className="flex flex-row pl-1 gap-6">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="radio"
                      name="isTaxExempt"
                      checked={state.isTaxExempt === true}
                      onChange={() => update({ isTaxExempt: true })}
                      className="h-4 w-4 cursor-pointer rounded-full bg-paper-light focus:ring-sage border-edge text-sage"
                    />
                    <span className="text-sm text-ink">Yes</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="radio"
                      name="isTaxExempt"
                      checked={state.isTaxExempt === false}
                      onChange={() => update({ isTaxExempt: false })}
                      className="h-4 w-4 cursor-pointer rounded-full bg-paper-light focus:ring-sage border-edge text-sage"
                    />
                    <span className="text-sm text-ink">No</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Individual: Plain text org name + optional website */}
          {!isNonprofit && (
            <>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">What is your organization&apos;s name?</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => update({ orgName: e.target.value })}
                  placeholder="e.g. Jane's Cancer Fund"
                  className={`${INPUT_CLASS} ${availability.nameError ? 'border-status-red-text/50' : ''}`}
                  autoComplete="off"
                  aria-invalid={Boolean(availability.nameError)}
                  aria-describedby={availability.nameError ? 'onb-org-name-error' : undefined}
                />
                {availability.nameError && (
                  <p id="onb-org-name-error" className="text-xs text-status-red-text" role="alert">
                    {availability.nameError}
                  </p>
                )}
              </div>

              {/* PE-2048: URL name (slug), auto-suggested from the org name */}
              <OrgUrlNameField
                state={state}
                update={update}
                inputClass={INPUT_CLASS}
                error={availability.slugError}
                requestError={availability.requestError}
                checking={availability.checking}
                onRetry={availability.retry}
              />

              {/* Website — optional, triggers enrichment (logo + description) on Continue */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">Website (recommended)</label>
                <input
                  type="text"
                  inputMode="url"
                  value={state.websiteUrl || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    enrichRequestIdRef.current += 1;
                    const patch = { websiteUrl: v, hasWebsite: !!v.trim(), websiteEnrichStatus: 'idle' };
                    if (!v.trim()) {
                      patch.ogImage = '';
                      patch.favicon = '';
                      patch.suggestedBrandColor = '';
                    }
                    update(patch);
                  }}
                  placeholder="yourorganization.org"
                  className={INPUT_CLASS}
                  autoComplete="off"
                />
              </div>
            </>
          )}

          {/* Cause category — shown for ALL entity types */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-ink-secondary">What type of organization are you?</label>
            <select
              value={causeCategory}
              onChange={(e) => update({ causeCategory: e.target.value })}
              className={`${SELECT_CLASS}${!causeCategory ? ' text-ink-muted' : ''}`}
              aria-label="Organization type"
            >
              <option value="">Select category…</option>
              <option value="FAITH_BASED">Faith-Based</option>
              <option value="POVERTY_BASIC_NEEDS">Poverty & Basic Needs</option>
              <option value="HEALTHCARE_MEDICAL">Healthcare & Medical Aid</option>
              <option value="MENTAL_HEALTH">Mental Health</option>
              <option value="HOUSING_SHELTER">Housing & Shelter</option>
              <option value="EDUCATION">Education</option>
              <option value="YOUTH_DEVELOPMENT">Youth Development</option>
              <option value="SENIOR_SERVICES">Senior Services</option>
              <option value="ECONOMIC_WORKFORCE">Economic & Workforce Development</option>
              <option value="COMMUNITY_DEVELOPMENT">Community Development & Local Support</option>
              <option value="CIVIL_RIGHTS_ADVOCACY">Civil Rights & Advocacy</option>
              <option value="PUBLIC_POLICY">Public Policy</option>
              <option value="INTERNATIONAL_DEVELOPMENT">Global & International Development</option>
              <option value="ENVIRONMENT">Environment</option>
              <option value="ANIMAL_WELFARE">Animal Welfare</option>
              <option value="ARTS_CULTURE">Arts & Culture</option>
              <option value="SCIENCE_TECH">Science & Technology</option>
              <option value="ORPHANS_CHILDREN">Orphans & Children</option>
              <option value="VETERANS_MILITARY">Veterans & Military Families</option>
            </select>
          </div>
        </div>

       
      </div>
    </OnboardingStepLayout>
  );
}
